/**
 * Camera Orientation Tool
 *
 * Provides two methods for aligning the camera with room geometry:
 * 1. Manual two-point alignment: Click two points to define a horizontal line,
 *    the camera will rotate to align with that line.
 * 2. Auto-detect: Right-click to automatically detect and apply room orientation
 *    based on dominant wall directions.
 *
 * Icon: undo-dot.svg
 */

import { FloorplanToolBase } from './FloorplanToolBase';
import { Viewer } from '../../../viewer/viewer';
import { IPersona, IViewConfig } from '../../types';
import { ViewModes } from '../../helpers';
import { CameraAlignmentTool } from '../../../utils/CameraAlignmentTool';
import { RoomOrientationDebugWidget } from '../../../utils/RoomOrientationDebugWidget';
import * as THREE from 'three';

export class CameraOrientationTool extends FloorplanToolBase {
  readonly name = 'camera_orientation';

  /** Rotation works from any click geometry — a floor selection only refines
   *  the anchor height, so the tool stays available without one. */
  override readonly requiresFloor = false;

  /** Camera alignment tool for manual two-point alignment */
  private cameraAlignmentTool: CameraAlignmentTool;

  /** Debug widget for visualizing room orientation detection */
  private debugWidget: RoomOrientationDebugWidget | null = null;

  /** Track mouse down position to detect drag vs click */
  private mouseDownPos: THREE.Vector2 | null = null;

  /** Threshold in pixels to distinguish click from drag */
  private readonly DRAG_THRESHOLD = 5;

  /** Whether we're in click-click mode (first point placed, waiting for second click) */
  private waitingForSecondClick = false;

  /** Whether 3D view should be included (when debug is enabled) */
  private include3DView = false;

  constructor(viewer: Viewer, persona: IPersona) {
    super(viewer, persona);

    // Initialize camera alignment tool
    this.cameraAlignmentTool = new CameraAlignmentTool(viewer);
  }

  protected onEnable(): void {
    // Call base class to set up floor selection handling and isolation
    super.onEnable();

    this.viewer.renderArea.style.cursor = 'crosshair';

    // Dispatch state so quest registry shows the default tooltip
    this.dispatchStateChanged({ phase: 'default' });

    // Add room orientation debug widget to debug overlay
    if (this.viewer.debugOverlay && !this.debugWidget) {
      this.debugWidget = new RoomOrientationDebugWidget(this.viewer);
      this.debugWidget.setDebugStateChangeCallback((anyEnabled) => {
        this.include3DView = anyEnabled;
        // Refresh available views by calling setView on persona
        // This triggers the UI to update with the new view config
        const currentView = this.persona.getCurrentView();
        if (currentView) {
          this.persona.setView(currentView.id);
        }
      });
      this.viewer.debugOverlay.addWidget(this.debugWidget);
    }
  }

  protected onDisable(): void {
    this.viewer.renderArea.style.cursor = '';

    // Cancel any ongoing alignment
    if (this.cameraAlignmentTool.isAlignmentActive()) {
      this.cameraAlignmentTool.cancel();
    }

    // Remove room orientation debug widget from debug overlay
    if (this.viewer.debugOverlay && this.debugWidget) {
      this.debugWidget.setDebugStateChangeCallback(null);
      this.debugWidget.clearDebugVisualizations();
      this.viewer.debugOverlay.removeWidget(this.debugWidget.name);
      this.debugWidget = null;
    }

    // Clear wall visualization
    this.clearWallVisualization();

    // Reset state
    this.mouseDownPos = null;
    this.waitingForSecondClick = false;
    this.include3DView = false;

    // Call base class to clean up floor isolation and event listeners
    super.onDisable();
  }

  /**
   * Override view configuration for 2D orthographic views
   * Default to X-Ray mode for best visibility of room geometry
   * When debug visualizations are enabled, 3D view is added
   */
  getViewConfig(): IViewConfig {
    const views = [ViewModes.XRAY_2D, ViewModes.ORTHOGRAPHIC_2D_TOP];

    // The wall/design tabs offer the Plan view on every tool — keep it
    // available while this shared camera tool is active there, without
    // leaking it into the other tabs/personas that host this tool.
    const modeId = this.persona.getCurrentMode()?.id;
    if (modeId === 'wall' || modeId === 'design') {
      views.splice(1, 0, ViewModes.PLAN_2D_SKELETON);
    }

    // Add 3D view when debug visualization is enabled
    if (this.include3DView) {
      views.push(ViewModes.PERSPECTIVE_3D_RGB);
    }

    return {
      views,
      defaultViewId: 'xray_2d',
    };
  }

  /**
   * Check if currently in 3D view (tools disabled in 3D, only for debug visualization)
   */
  private isIn3DView(): boolean {
    const currentView = this.persona.getCurrentView();
    return currentView?.id === 'perspective_3d_rgb';
  }

  /**
   * Handle mouse down - track position for drag detection
   */
  public onMouseDown(event: MouseEvent): void {
    // Tools disabled in 3D view (only for debug visualization)
    if (this.isIn3DView()) return;

    // Ignore shift/ctrl - these are for camera panning
    if (event.shiftKey || event.ctrlKey) return;

    // Only handle left click
    if (event.button !== 0) return;

    // Store mouse down position for drag detection
    this.mouseDownPos = this.getMousePosition(event);

    // If waiting for second click, don't start new alignment on mouse down
    if (this.waitingForSecondClick) return;

    // Start alignment on mouse down (will be used for drag mode)
    if (!this.cameraAlignmentTool.isAlignmentActive()) {
      const worldPos = this.mouseDownPos ? this.screenToWorld(this.mouseDownPos) : null;
      if (worldPos) {
        this.cameraAlignmentTool.start(worldPos);
      }
    }
  }

  /**
   * Handle mouse move - update alignment preview
   */
  public onMouseMove(event: MouseEvent): void {
    // Tools disabled in 3D view (only for debug visualization)
    if (this.isIn3DView()) return;

    if (!this.cameraAlignmentTool.isAlignmentActive()) return;

    const mouse = this.getMousePosition(event);
    if (!mouse) return;

    const worldPos = this.screenToWorld(mouse);
    if (worldPos) {
      this.cameraAlignmentTool.updateEndPoint(worldPos);
    }
  }

  /**
   * Handle mouse up - complete alignment or handle click mode
   */
  public onMouseUp(event: MouseEvent): void {
    // Tools disabled in 3D view (only for debug visualization)
    if (this.isIn3DView()) return;

    // Only handle left click
    if (event.button !== 0) return;

    const mouse = this.getMousePosition(event);
    if (!mouse) return;

    // Check if this was a drag or a click
    const isDrag = this.mouseDownPos && this.isSignificantMove(this.mouseDownPos, mouse);

    if (this.waitingForSecondClick) {
      // Second click in click-click mode - complete alignment
      const worldPos = this.screenToWorld(mouse);
      if (worldPos && this.cameraAlignmentTool.isAlignmentActive()) {
        this.cameraAlignmentTool.complete(worldPos);
      }
      this.waitingForSecondClick = false;
    } else if (isDrag) {
      // Drag mode - complete alignment at current position
      if (this.cameraAlignmentTool.isAlignmentActive()) {
        const worldPos = this.screenToWorld(mouse);
        if (worldPos) {
          this.cameraAlignmentTool.complete(worldPos);
        }
      }
    } else {
      // Click mode - first click places point, wait for second click
      // The alignment was already started in onMouseDown, just mark we're waiting
      if (this.cameraAlignmentTool.isAlignmentActive()) {
        this.waitingForSecondClick = true;
      }
    }

    this.mouseDownPos = null;
  }

  /**
   * Check if mouse moved significantly (beyond drag threshold)
   */
  private isSignificantMove(start: THREE.Vector2, end: THREE.Vector2): boolean {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    return Math.sqrt(dx * dx + dy * dy) > this.DRAG_THRESHOLD;
  }

  /**
   * Handle right-click - auto-detect room orientation
   */
  public onRightClick(event: MouseEvent): void {
    // Tools disabled in 3D view (only for debug visualization)
    if (this.isIn3DView()) return;

    event.preventDefault();

    // Cancel any ongoing manual alignment
    if (this.cameraAlignmentTool.isAlignmentActive()) {
      this.cameraAlignmentTool.cancel();
    }
    this.waitingForSecondClick = false;

    // Get click position in world coordinates
    const mouse = this.getMousePosition(event);
    const clickWorldPos = mouse ? this.screenToWorld(mouse) : null;

    // Auto-detect and apply room orientation using click position
    this.applyAutoOrientation(clickWorldPos);
  }

  /**
   * Handle keyboard shortcuts
   */
  public onKeyDown(event: KeyboardEvent): void {
    // Tools disabled in 3D view (only for debug visualization)
    if (this.isIn3DView()) return;

    // ESC to cancel alignment
    if (event.key === 'Escape') {
      if (this.cameraAlignmentTool.isAlignmentActive() || this.waitingForSecondClick) {
        this.cameraAlignmentTool.cancel();
        this.waitingForSecondClick = false;
        event.preventDefault();
        event.stopPropagation();
      }
      return;
    }

    // 'R' key to reset/auto-detect orientation
    if (event.key === 'r' || event.key === 'R') {
      event.preventDefault();
      this.applyAutoOrientation();
    }
  }

  /**
   * Auto-detect room orientation and apply it to the camera
   * @param clickPosition - Optional world position where user clicked (X/Y used as center for ray casting)
   */
  private async applyAutoOrientation(clickPosition: THREE.Vector3 | null = null): Promise<void> {
    const cameraSettingsManager = this.viewer.cameraSettingsManager;
    if (!cameraSettingsManager) {
      this.warn('CameraSettingsManager not available');
      return;
    }

    // Reset the cache to force re-detection
    cameraSettingsManager.resetRoomOrientationCache();

    // Calculate center point for ray casting
    // X/Y from click position, Z from floor mid-height
    let centerPoint: THREE.Vector3 | null = null;

    if (clickPosition) {
      // Calculate mid Z between floor and ceiling
      let midZ: number;
      if (this.currentFloorFloorPlane && this.currentFloorCeilingPlane) {
        // Both floor and ceiling available - use their mid-point
        midZ = (this.currentFloorFloorPlane.height + this.currentFloorCeilingPlane.height) / 2;
      } else if (this.currentFloorFloorPlane) {
        // Only floor available - use floor height + default room height / 2
        midZ = this.currentFloorFloorPlane.height + 1.5;
      } else {
        // No floor info - use click position Z (will fall back to bounding box in manager)
        midZ = clickPosition.z;
      }

      centerPoint = new THREE.Vector3(clickPosition.x, clickPosition.y, midZ);
    }

    // Detect room orientation using calculated center point
    const result = await cameraSettingsManager.detectRoomOrientation(centerPoint);

    if (result === null) {
      this.warn('Could not detect room orientation');
      return;
    }

    // Apply + persist through the orientation manager: writes the heading on
    // the top-down class's canonical axis (ROLL, yaw pinned to 0 — the axes
    // are gimbal-degenerate at pitch −90°). The old code applied the angle
    // as YAW while every restore path consumed the saved value as ROLL, so
    // a 2D→3D→2D round trip came back with a different on-screen
    // orientation.
    this.viewer.cameraOrientationManager.setUserHeading('top_down_2d', result.angle, 'auto_orientation');

    // Visualize the detected wall plane temporarily
    this.visualizeDetectedWall(result.wallInliers, result.wallNormal);

    this.log(`Applied auto orientation: ${((result.angle * 180) / Math.PI).toFixed(1)}°`);
  }

  /** Temporary visualization group for detected wall */
  private wallVisualizationGroup: THREE.Group | null = null;

  /**
   * Visualize the detected wall plane with a line and normal arrow
   */
  private visualizeDetectedWall(inliers: THREE.Vector3[], normal: THREE.Vector3): void {
    // Remove previous visualization
    this.clearWallVisualization();

    if (inliers.length === 0) return;

    // Create a group for the visualization
    this.wallVisualizationGroup = new THREE.Group();
    this.wallVisualizationGroup.name = 'camera-orientation-wall-viz';

    // Calculate center and extents of inliers
    const center = new THREE.Vector3();
    for (const point of inliers) {
      center.add(point);
    }
    center.divideScalar(inliers.length);

    // Calculate the wall direction (perpendicular to normal in XY plane)
    // The wall direction is the direction along the wall (tangent to the plane)
    const wallDirection = new THREE.Vector3(-normal.y, normal.x, 0).normalize();

    // Find the extent of the wall by projecting inliers onto the wall direction
    let minProjection = Infinity;
    let maxProjection = -Infinity;
    for (const point of inliers) {
      const projection = point.clone().sub(center).dot(wallDirection);
      minProjection = Math.min(minProjection, projection);
      maxProjection = Math.max(maxProjection, projection);
    }

    // Add some padding to make the line extend slightly beyond the points
    const padding = 0.5;
    minProjection -= padding;
    maxProjection += padding;

    // Calculate line endpoints
    const lineStart = center.clone().add(wallDirection.clone().multiplyScalar(minProjection));
    const lineEnd = center.clone().add(wallDirection.clone().multiplyScalar(maxProjection));

    // Create the wall line (purple)
    const purple = 0x9933ff;
    const lineGeometry = new THREE.BufferGeometry().setFromPoints([lineStart, lineEnd]);
    const lineMaterial = new THREE.LineBasicMaterial({
      color: purple,
      linewidth: 3, // Note: linewidth > 1 only works in WebGL with certain configurations
    });
    const wallLine = new THREE.Line(lineGeometry, lineMaterial);
    this.wallVisualizationGroup.add(wallLine);

    // Create normal arrow (purple, pointing outward from wall)
    const arrowLength = 2.0;
    const arrowHelper = new THREE.ArrowHelper(
      normal.clone().normalize(),
      center,
      arrowLength,
      purple,
      0.3, // Head length
      0.15, // Head width
    );
    this.wallVisualizationGroup.add(arrowHelper);

    // Add to scene
    this.viewer.scene.scene.add(this.wallVisualizationGroup);
  }

  /**
   * Clear the wall visualization
   */
  private clearWallVisualization(): void {
    if (this.wallVisualizationGroup) {
      this.viewer.scene.scene.remove(this.wallVisualizationGroup);
      // Dispose geometries and materials
      this.wallVisualizationGroup.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          if (child.material instanceof THREE.Material) {
            child.material.dispose();
          }
        }
      });
      this.wallVisualizationGroup = null;
    }
  }

  /**
   * Get mouse position relative to the render area
   */
  private getMousePosition(event: MouseEvent): THREE.Vector2 | null {
    const rect = this.viewer.renderArea.getBoundingClientRect();
    return new THREE.Vector2(event.clientX - rect.left, event.clientY - rect.top);
  }

  /**
   * Convert screen coordinates to world position
   */
  private screenToWorld(screen: THREE.Vector2): THREE.Vector3 | null {
    const camera = this.viewer.scene.getActiveCamera();
    const viewportWidth = this.viewer.renderArea.clientWidth;
    const viewportHeight = this.viewer.renderArea.clientHeight;

    const ndcX = (screen.x / viewportWidth) * 2 - 1;
    const ndcY = -(screen.y / viewportHeight) * 2 + 1;

    // If there's an associated plane, unproject directly onto it
    const associatedPlane = this.viewer.viewerMode.associatedPlane;
    if (associatedPlane) {
      // Unproject the screen point to two positions in world space (near and far)
      const nearPoint = new THREE.Vector3(ndcX, ndcY, -1).unproject(camera);
      const farPoint = new THREE.Vector3(ndcX, ndcY, 1).unproject(camera);

      // Create a ray from near to far
      const rayDirection = new THREE.Vector3().subVectors(farPoint, nearPoint).normalize();
      const ray = new THREE.Ray(nearPoint, rayDirection);

      // Intersect with the associated plane
      const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(associatedPlane.getNormal(), associatedPlane.position.clone());
      const intersection = ray.intersectPlane(plane, new THREE.Vector3());
      if (intersection) {
        return intersection;
      }
    }

    // Fallback: unproject at z=0 (for top-down view)
    const nearPoint = new THREE.Vector3(ndcX, ndcY, -1).unproject(camera);
    const farPoint = new THREE.Vector3(ndcX, ndcY, 1).unproject(camera);
    const rayDirection = new THREE.Vector3().subVectors(farPoint, nearPoint).normalize();
    const ray = new THREE.Ray(nearPoint, rayDirection);

    // Intersect with z=0 plane
    const groundPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
    const intersection = ray.intersectPlane(groundPlane, new THREE.Vector3());
    if (intersection) {
      return intersection;
    }

    return null;
  }

  /**
   * Clean up resources
   */
  dispose(): void {
    this.cameraAlignmentTool.dispose();

    // Clean up debug widget if still active
    if (this.viewer.debugOverlay && this.debugWidget) {
      this.debugWidget.clearDebugVisualizations();
      this.viewer.debugOverlay.removeWidget(this.debugWidget.name);
      this.debugWidget = null;
    }

    // Clear wall visualization
    this.clearWallVisualization();

    super.dispose();
  }
}
